import React, { useState } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight, Download, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

interface AvailabilityCalendarProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSlot?: (date: Date, time: string) => void;
  serviceName?: string;
  providerName?: string;
  providerEmail?: string;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  isOpen,
  onClose,
  onSelectSlot,
  serviceName = "Service Afspraak",
  providerName = "WOM Provider",
  providerEmail = "info@womplaza.nl"
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Mock available slots for demonstration
  const mockAvailableSlots: { [key: string]: string[] } = {
    '2024-12-16': ['10:00', '11:00', '14:00', '15:00'],
    '2024-12-18': ['10:00', '13:00', '14:00', '16:00'],
    '2024-12-19': ['11:00', '13:00', '15:00', '16:00'],
    '2024-12-23': ['10:00', '11:00', '14:00'],
    '2024-12-25': ['13:00', '14:00', '15:00', '16:00'],
    '2024-12-26': ['10:00', '11:00', '13:00', '14:00'],
    '2024-12-30': ['10:00', '11:00', '14:00', '15:00'],
    '2025-01-02': ['11:00', '13:00', '15:00', '16:00'],
    '2025-01-06': ['10:00', '13:00', '14:00'],
    '2025-01-08': ['10:00', '11:00', '15:00', '16:00']
  };

  const monthNames = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
  ];

  const dayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];
    // Add empty slots for days before month starts (adjust for Monday start)
    for (let i = 0; i < (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1); i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateAvailable = (date: Date) => {
    const dateKey = formatDateKey(date);
    return mockAvailableSlots[dateKey] && mockAvailableSlots[dateKey].length > 0;
  };

  const getAvailableTimesForDate = (date: Date) => {
    const dateKey = formatDateKey(date);
    return mockAvailableSlots[dateKey] || [];
  };

  const handleDateSelect = (date: Date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
      console.log('Date selected:', date.toLocaleDateString('nl-NL'));
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    console.log('Time selected:', time);
  };

  // Apple Calendar Integration - Generate .ics file
  const generateICSFile = (date: Date, time: string) => {
    const startDateTime = new Date(date);
    const [hours, minutes] = time.split(':');
    startDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(startDateTime.getHours() + 1); // 1 hour duration

    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//WOM Plaza//Appointment//NL
CALSCALE:GREGORIAN
METHOD:REQUEST
BEGIN:VEVENT
UID:${Date.now()}@womplaza.nl
DTSTART:${formatICSDate(startDateTime)}
DTEND:${formatICSDate(endDateTime)}
SUMMARY:${serviceName} - ${providerName}
DESCRIPTION:Afspraak voor ${serviceName} met ${providerName} via WOM Plaza
LOCATION:Online/Op locatie (details volgen)
ORGANIZER;CN=${providerName}:mailto:${providerEmail}
STATUS:CONFIRMED
TRANSP:OPAQUE
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Herinnering: ${serviceName} over 15 minuten
END:VALARM
END:VEVENT
END:VCALENDAR`;

    return icsContent;
  };

  const downloadICSFile = () => {
    if (!selectedDate || !selectedTime) return;

    const icsContent = generateICSFile(selectedDate, selectedTime);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `WOM-afspraak-${selectedDate.toISOString().split('T')[0]}-${selectedTime.replace(':', '')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('📅 Agenda bestand gedownload! Open het om toe te voegen aan Apple Agenda.');
  };
  const handleConfirmBooking = () => {
    if (selectedDate && selectedTime) {
      console.log('Booking confirmed:', { date: selectedDate, time: selectedTime });
      toast.success(`Afspraak bevestigd voor ${selectedDate.toLocaleDateString('nl-NL')} om ${selectedTime}`);
      if (onSelectSlot) {
        onSelectSlot(selectedDate, selectedTime);
      }

      // Auto-download calendar file
      downloadICSFile();
      // Reset selections and close
      setSelectedDate(null);
      setSelectedTime(null);
      onClose();
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    // Reset selections when changing month
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate comparison
  const availableTimes = selectedDate ? getAvailableTimesForDate(selectedDate) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-white border-2 border-gray-200 shadow-2xl" style={{ backgroundColor: 'white' }}>
        <div className="absolute inset-0 bg-white rounded-lg" style={{ backgroundColor: 'white', zIndex: -1 }}></div>
        <DialogHeader className="bg-stone-100 -m-6 mb-0 p-6 rounded-t-lg border-b border-stone-200">
          <DialogTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-stone-700" />
            Selecteer Datum & Tijd
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 p-6 bg-white">
          {/* Calendar Header */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth('prev')}
              className="p-2 bg-stone-50 hover:bg-stone-100 border border-stone-200"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="text-lg font-medium text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth('next')}
              className="p-2 bg-stone-50 hover:bg-stone-100 border border-stone-200"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="space-y-2 bg-white p-4 rounded-lg border border-stone-200">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2 bg-stone-50 rounded">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                if (!date) {
                  return <div key={index} className="h-10 bg-white" />;
                }

                const dateForComparison = new Date(date);
                dateForComparison.setHours(0, 0, 0, 0);
                const isToday = dateForComparison.getTime() === today.getTime();
                const isPast = dateForComparison < today;
                const isAvailable = isDateAvailable(date);
                const isSelected = selectedDate?.toDateString() === date.toDateString();

                return (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(date)}
                    disabled={isPast || !isAvailable}
                    className={`
                      h-10 w-10 text-sm rounded-lg transition-all font-medium border-2
                      ${isSelected 
                        ? 'bg-stone-600 text-white border-stone-700 shadow-md' 
                        : isAvailable && !isPast
                          ? 'bg-stone-200 text-stone-800 border-stone-300 hover:bg-stone-300' 
                          : isPast
                            ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
                            : 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                      }
                      ${isToday && !isSelected ? 'ring-2 ring-stone-400' : ''}
                    `}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Available Times */}
          {selectedDate && (
            <div className="space-y-3 bg-white p-4 rounded-lg border border-stone-200">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-stone-700" />
                <h4 className="font-medium text-gray-900">
                  Beschikbare tijden voor {selectedDate.toLocaleDateString('nl-NL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                  })}
                </h4>
              </div>
              {availableTimes.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {availableTimes.map(time => (
                    <button
                      key={time}
                      onClick={() => handleTimeSelect(time)}
                      className={`
                        px-3 py-2 text-sm rounded-lg border-2 transition-all font-medium
                        ${selectedTime === time
                          ? 'bg-stone-600 text-white border-stone-700 shadow-md'
                          : 'bg-white text-stone-800 border-stone-300 hover:bg-stone-100'
                        }
                      `}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-stone-50 rounded-lg border border-stone-200">
                  <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    Geen beschikbare tijden voor deze datum
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Selected Summary */}
          {selectedDate && selectedTime && (
            <div className="bg-stone-100 rounded-lg p-4 border-2 border-stone-300">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-stone-700" />
                Geselecteerde afspraak:
              </h4>
              <div className="space-y-1">
                <p className="text-sm text-stone-800 font-medium">
                  📅 {selectedDate.toLocaleDateString('nl-NL', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-sm text-stone-800 font-medium">
                  🕐 {selectedTime}
                </p>
              </div>
            </div>
          )}

          {/* Apple Calendar Integration Info */}
          {selectedDate && selectedTime && (
            <div className="bg-stone-50 rounded-lg p-3 border-2 border-stone-200">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-4 w-4 text-stone-700" />
                <span className="text-sm font-medium text-gray-900">Apple Agenda Integratie</span>
              </div>
              <p className="text-xs text-stone-700">
                Bij bevestiging wordt automatisch een agenda-bestand gedownload dat je kunt openen in Apple Agenda, Google Calendar, of Outlook.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 bg-white border-2 border-stone-300 text-stone-800 hover:bg-stone-50"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleConfirmBooking}
              disabled={!selectedDate || !selectedTime}
              className="flex-1 bg-stone-600 text-white border-2 border-stone-700 hover:bg-stone-700 disabled:bg-gray-300 disabled:border-gray-400 disabled:cursor-not-allowed"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Bevestigen & Toevoegen aan Agenda
            </Button>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 text-xs text-gray-600 pt-2 border-t-2 border-stone-200 bg-stone-50 p-3 rounded-lg">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-stone-200 border-2 border-stone-300 rounded"></div>
              <span>Beschikbaar</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-100 border-2 border-gray-200 rounded"></div>
              <span>Niet beschikbaar</span>
            </div>
            {selectedDate && (
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-stone-600 border-2 border-stone-700 rounded"></div>
                <span>Geselecteerd</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvailabilityCalendar;